/**
 * RiskDashboard — leadership-facing project health view (CR 2026-09-18 §4.10).
 * See docs/phase2/change-requests/2026-09-18-project-risk-dashboard-cr.md.
 *
 * Read-only, point-in-time snapshot. Aggregates four leading risk signals
 * (Schedule / Budget / Scope / Quality), meeting health, phase timeline, and open
 * escalations into a single scannable card. Green is a valid, fully-rendered state.
 *
 * Accessibility (WCAG 2.1 AA): every colour-coded element carries a text label and
 * an aria-label — colour is never the sole carrier of meaning.
 */

import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Clock,
  ExternalLink,
  Flag,
  Info,
  RotateCw,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

import {
  useProjectRisk,
  type EscalationSummary,
  type MeetingHealth,
  type RiskLevel,
  type RiskSignal,
} from '@/hooks/useProjectRisk';
import { cn } from '@/lib/utils';
import { phaseLabel } from '@/lib/phases';
import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip } from '@/components/ui/tooltip';

// ─── Level presentation ────────────────────────────────────────────────────────
// Green/red use design-system semantic tokens (--color-success / --color-error).
// Yellow uses the shared amber (#c98a00) already used across the risk/attention surfaces.

interface LevelConfig {
  dot: string;
  text: string;
  border: string;
  badge: BadgeVariant;
  icon: LucideIcon;
  /** Human status word — spoken by screen readers, never colour alone. */
  word: string;
}

const LEVEL_CONFIG: Record<RiskLevel, LevelConfig> = {
  green: {
    dot: 'bg-success',
    text: 'text-success',
    border: 'border-success/30',
    badge: 'success',
    icon: TrendingUp,
    word: 'healthy',
  },
  yellow: {
    dot: 'bg-[#c98a00]',
    text: 'text-[#c98a00]',
    border: 'border-[#c98a00]/30',
    badge: 'warning',
    icon: AlertTriangle,
    word: 'needs attention',
  },
  red: {
    dot: 'bg-error',
    text: 'text-error',
    border: 'border-error/30',
    badge: 'danger',
    icon: AlertCircle,
    word: 'at risk',
  },
};

const OVERALL_LABEL: Record<RiskLevel, string> = {
  green: 'Healthy',
  yellow: 'Needs Attention',
  red: 'At Risk',
};

// Reuse the sentiment palette from MeetingIntelligenceCard for consistency.
const SENTIMENT_BADGE: Record<
  NonNullable<MeetingHealth['sentiment']>,
  { label: string; variant: BadgeVariant }
> = {
  positive: { label: 'Positive', variant: 'success' },
  neutral: { label: 'Neutral', variant: 'neutral' },
  negative: { label: 'Negative', variant: 'warning' },
  at_risk: { label: 'At Risk', variant: 'danger' },
};

const SEVERITY_BADGE: Record<EscalationSummary['severity'], BadgeVariant> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

// ─── Formatters ──────────────────────────────────────────────────────────────

function formatComputedAt(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Signal explanations (info tooltip) ─────────────────────────────────────
// The rules below mirror packages/gates/services/risk.service.ts. Keep them in sync
// when thresholds change there. "Right now" values come straight from signal.metrics.

type SignalKey = 'schedule' | 'budget' | 'scope' | 'quality';

interface SignalExplanation {
  how: string;
  thresholds: string[];
  now: string[];
}

function num(v: unknown): string {
  return typeof v === 'number' && Number.isFinite(v) ? String(v) : '—';
}

function yesNo(v: unknown): string {
  return v === true ? 'yes' : 'no';
}

function explainSignal(key: SignalKey, metrics: Record<string, unknown>): SignalExplanation {
  switch (key) {
    case 'schedule': {
      const expected =
        typeof metrics.expected_completion_date === 'string'
          ? formatDate(metrics.expected_completion_date)
          : 'not set';
      return {
        how: 'Days since the current phase started (last approved gate, or project creation if no gate yet), checked against the expected completion date.',
        thresholds: [
          'Green: 14 days or less in phase',
          'Yellow: 15 to 28 days',
          'Red: more than 28 days, or expected completion date has passed',
        ],
        now: [
          `${num(metrics.days_in_phase)} days in phase`,
          `Expected completion: ${expected}`,
          `Overdue: ${yesNo(metrics.overdue)}`,
        ],
      };
    }
    case 'budget': {
      const sow = metrics.sow_hours;
      const source =
        metrics.hours_source === 'weekly_report'
          ? `Hours from: weekly report, week ending ${
              typeof metrics.hours_as_of === 'string' ? formatDate(metrics.hours_as_of) : '—'
            }`
          : 'Hours from: project hours field (no submitted weekly report yet)';
      return {
        how: "Burn rate = hours consumed ÷ SOW hours. Hours consumed is the cumulative hours on the latest submitted weekly report, or the project's hours field if none is submitted yet.",
        thresholds: [
          'Green: under 75%',
          'Yellow: 75% to 90%, or SOW hours not set',
          'Red: over 90%, or any open date-change request',
        ],
        now:
          typeof sow === 'number'
            ? [
                `${num(metrics.hours_consumed)} of ${num(sow)} SOW hours = ${num(metrics.burn_rate_pct)}%`,
                `Open date-change requests: ${num(metrics.date_change_request_count ?? 0)}`,
                source,
              ]
            : [`${num(metrics.hours_consumed)} hours consumed`, 'SOW hours: not set', source],
      };
    }
    case 'scope':
      return {
        how: 'Counts open (non-rejected) date-change requests and checks the latest meeting analysis for this phase for missed scope topics (scope, deliverable, requirement, success).',
        thresholds: [
          'Green: no requests and no missed scope topic',
          'Yellow: 1 request, or a scope topic missed in the last meeting',
          'Red: 2+ requests, or at-risk sentiment together with a missed scope topic',
        ],
        now: [
          `Open change requests: ${num(metrics.change_request_count)}`,
          `Scope topic missed in last meeting: ${yesNo(metrics.missing_scope_topic_last_meeting)}`,
          `Scope disagreement flagged: ${yesNo(metrics.scope_disagreement)}`,
        ],
      };
    case 'quality':
      return {
        how: "Compares this phase's critical topics with the topics missed in the latest meeting analysis, and counts this phase's meetings with at-risk sentiment.",
        thresholds: [
          'Green: no critical topic missed and fewer than 2 at-risk meetings',
          'Yellow: 1 critical topic missed',
          'Red: 2+ critical topics missed, or 2+ at-risk meetings',
        ],
        now: [
          `Critical topics missed in last meeting: ${num(metrics.critical_topics_missing_last_meeting)}`,
          `At-risk meetings this phase: ${num(metrics.at_risk_meeting_count)}`,
        ],
      };
  }
}

function TooltipSection({ title, items }: { title: string; items: string[] }): JSX.Element {
  return (
    <span className="mt-1.5 block">
      <span className="block font-semibold text-text-primary">{title}</span>
      {items.map((item) => (
        <span key={item} className="block text-text-secondary">
          • {item}
        </span>
      ))}
    </span>
  );
}

/** Small focusable "i" trigger; the tooltip opens on hover and on keyboard focus. */
function InfoTip({ label, content }: { label: string; content: React.ReactNode }): JSX.Element {
  return (
    <Tooltip content={content} width="wide">
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-text-secondary hover:text-text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={label}
      >
        <Info className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </Tooltip>
  );
}

function SignalInfoContent({
  label,
  explanation,
}: {
  label: string;
  explanation: SignalExplanation;
}): JSX.Element {
  return (
    <span className="block leading-snug">
      <span className="block font-semibold text-text-primary">How {label} is calculated</span>
      <span className="mt-0.5 block text-text-secondary">{explanation.how}</span>
      <TooltipSection title="Thresholds" items={explanation.thresholds} />
      <TooltipSection title="Right now" items={explanation.now} />
    </span>
  );
}

/** Coverage-bar fill colour by percentage (mirrors the level palette). */
function coverageFill(pct: number): string {
  if (pct >= 80) return 'bg-success';
  if (pct >= 50) return 'bg-[#c98a00]';
  return 'bg-error';
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

// ─── Signal card ───────────────────────────────────────────────────────────────

interface RiskSignalCardProps {
  signalKey: SignalKey;
  label: string;
  signal: RiskSignal;
}

function RiskSignalCard({ signalKey, label, signal }: RiskSignalCardProps): JSX.Element {
  const cfg = LEVEL_CONFIG[signal.level];
  const Icon = cfg.icon;
  const explanation = explainSignal(signalKey, signal.metrics);

  return (
    <div
      className={cn('rounded-lg border bg-white p-2.5 transition-shadow hover:shadow-sm', cfg.border)}
      role="group"
      aria-label={`${label}: ${cfg.word}. ${signal.reason}`}
    >
      <div className="flex items-center gap-2">
        <span
          className={cn('h-2 w-2 shrink-0 rounded-full', cfg.dot)}
          role="img"
          aria-label={`${label}: ${cfg.word}`}
        />
        <span className="text-xs font-semibold uppercase tracking-wide text-text-primary">
          {label}
        </span>
        <InfoTip
          label={`How ${label} is calculated`}
          content={<SignalInfoContent label={label} explanation={explanation} />}
        />
        <Icon className={cn('ml-auto h-4 w-4 shrink-0', cfg.text)} aria-hidden="true" />
      </div>
      <p className="mt-0.5 text-xs leading-tight text-text-secondary">{signal.reason}</p>
    </div>
  );
}

// ─── Meeting health list ────────────────────────────────────────────────────────

function MeetingHealthRow({ meeting }: { meeting: MeetingHealth }): JSX.Element {
  const sentiment = meeting.sentiment ? SENTIMENT_BADGE[meeting.sentiment] : null;
  const pct = meeting.topics_covered_pct;

  return (
    <li className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        {/* Meeting title */}
        <p className="truncate text-sm font-medium text-text-primary" title={meeting.meeting_name}>
          {meeting.meeting_name}
        </p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-xs text-text-secondary">{formatDate(meeting.meeting_date)}</span>
          {/* Avoma recording link — rendered only when the API supplies avoma_link. */}
          {meeting.avoma_link && (
            <a
              href={meeting.avoma_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              aria-label={`Open the Avoma recording for ${meeting.meeting_name}`}
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
              Avoma
            </a>
          )}
        </div>
      </div>

      {sentiment ? (
        <Badge variant={sentiment.variant} dot={false} className="shrink-0 text-xs">
          {sentiment.label}
        </Badge>
      ) : (
        <span className="shrink-0 text-xs italic text-text-secondary">Analysis pending</span>
      )}

      <div className="w-28 shrink-0">
        {pct !== null ? (
          <>
            <div className="mb-1 flex items-center justify-between">
              <span className="text-[11px] text-text-secondary">Topics</span>
              <span className="text-[11px] font-medium text-text-primary">{pct}%</span>
            </div>
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-outlines"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${meeting.meeting_name}: ${pct}% of topics covered`}
            >
              <div
                className={cn('h-full rounded-full', coverageFill(pct))}
                style={{ width: `${pct}%` }}
              />
            </div>
          </>
        ) : (
          <span className="text-[11px] italic text-text-secondary">No coverage data</span>
        )}
      </div>
    </li>
  );
}

function MeetingHealthList({
  meetings,
  lastMeetingAtRisk,
}: {
  meetings: MeetingHealth[];
  lastMeetingAtRisk: boolean;
}): JSX.Element {
  return (
    <section aria-label="Meeting health" className="space-y-2">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
        Meeting health
      </h4>

      {lastMeetingAtRisk && (
        <div
          className="flex items-start gap-2 rounded-md border border-[#c98a00]/40 bg-badge-yellow-bg p-2.5 text-xs text-[#8a5e00]"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#c98a00]" aria-hidden="true" />
          <span>Last meeting flagged at-risk — review transcript.</span>
        </div>
      )}

      {meetings.length > 0 ? (
        <ul className="divide-y divide-outlines">
          {meetings.slice(0, 3).map((m) => (
            <MeetingHealthRow key={m.meeting_id} meeting={m} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-text-secondary">No completed meetings in this phase yet.</p>
      )}
    </section>
  );
}

// ─── Escalations list ────────────────────────────────────────────────────────

function EscalationsList({ escalations }: { escalations: EscalationSummary[] }): JSX.Element {
  return (
    <section aria-label="Open escalations" className="space-y-2">
      <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        <Flag className="h-3.5 w-3.5 text-error" aria-hidden="true" />
        Open escalations ({escalations.length})
      </h4>
      <ul className="space-y-2">
        {escalations.map((e) => (
          <li
            key={e.id}
            className="flex items-start gap-3 rounded-md border border-outlines bg-white p-2.5"
          >
            <Badge variant={SEVERITY_BADGE[e.severity]} dot={false} className="shrink-0 text-xs capitalize">
              {e.severity}
            </Badge>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-text-primary" title={e.description}>
                {truncate(e.description, 80)}
              </p>
              <p className="text-xs text-text-secondary">
                Raised {formatDate(e.raised_date)}
                {e.raised_by ? ` · ${e.raised_by}` : ''}
              </p>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─── Card shell ────────────────────────────────────────────────────────────────

function RiskCardShell({
  children,
  headerRight,
}: {
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}): JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
            <Activity className="h-4 w-4 text-primary" aria-hidden="true" />
            Project Risk
          </h3>
          {headerRight}
        </div>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// ─── Component ─────────────────────────────────────────────────────────────────

export interface RiskDashboardProps {
  projectId: string;
}

const SIGNAL_ORDER: { key: SignalKey; label: string }[] = [
  { key: 'schedule', label: 'Schedule' },
  { key: 'budget', label: 'Budget' },
  { key: 'scope', label: 'Scope' },
  { key: 'quality', label: 'Quality' },
];

export function RiskDashboard({ projectId }: RiskDashboardProps): JSX.Element {
  const { risk, isLoading, isError, refetch } = useProjectRisk(projectId);

  // ── Loading — 4 skeleton cards + 3 skeleton meeting rows ──
  if (isLoading) {
    return (
      <RiskCardShell>
        <div className="space-y-5" role="status" aria-label="Loading project risk">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[76px] w-full rounded-lg" />
            ))}
          </div>
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </div>
      </RiskCardShell>
    );
  }

  // ── Error — retry ──
  if (isError || !risk) {
    return (
      <RiskCardShell>
        <div className="flex flex-col items-start gap-3">
          <p className="text-sm text-text-secondary">Failed to load risk data.</p>
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            Retry
          </Button>
        </div>
      </RiskCardShell>
    );
  }

  const overall = LEVEL_CONFIG[risk.overall_level];
  const timeline = risk.phase_timeline;

  return (
    <RiskCardShell
      headerRight={
        <div className="flex items-center gap-3">
          <span className="text-xs text-text-secondary" title={risk.computed_at}>
            Computed {formatComputedAt(risk.computed_at)}
          </span>
          <Badge variant={overall.badge} className="capitalize">
            {OVERALL_LABEL[risk.overall_level]}
          </Badge>
          <InfoTip
            label="How overall project risk is calculated"
            content={
              <span className="block leading-snug">
                <span className="block font-semibold text-text-primary">
                  How overall risk is calculated
                </span>
                <span className="mt-0.5 block text-text-secondary">
                  Overall takes the worst of the four signals: any red makes it At Risk, otherwise
                  any yellow makes it Needs Attention. It is recomputed each time this card loads.
                </span>
                <TooltipSection
                  title="Right now"
                  items={SIGNAL_ORDER.map(
                    ({ key, label }) => `${label}: ${LEVEL_CONFIG[risk.signals[key].level].word}`,
                  )}
                />
              </span>
            }
          />
        </div>
      }
    >
      <div className="space-y-5">
        {/* Signal row */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {SIGNAL_ORDER.map(({ key, label }) => (
            <RiskSignalCard key={key} signalKey={key} label={label} signal={risk.signals[key]} />
          ))}
        </div>

        {/* Compact phase-timeline line */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-text-secondary">
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="font-medium text-text-primary">{phaseLabel(timeline.current_phase)}</span>
          <span aria-hidden="true">•</span>
          <span>{timeline.days_in_phase} days in phase</span>
          <span aria-hidden="true">•</span>
          <span className={cn(timeline.expected_completion_overdue && 'font-medium text-error')}>
            Expected: {formatDate(timeline.expected_completion_date)}
            {timeline.expected_completion_overdue && ' (overdue)'}
          </span>
        </div>

        {/* Meeting health */}
        <MeetingHealthList
          meetings={risk.meeting_health}
          lastMeetingAtRisk={risk.last_meeting_at_risk}
        />

        {/* Escalations — only when at least one open escalation exists */}
        {risk.escalations.length > 0 && <EscalationsList escalations={risk.escalations} />}
      </div>
    </RiskCardShell>
  );
}

export default RiskDashboard;
